import { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { useDialog } from 'common/DialogProvider';
import { useNavigate } from 'react-router';

const SIGNER_STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  viewed: 'info',
  signed: 'success',
  rejected: 'error',
};

interface Props {
  documentId: number;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

interface SignerRow { email: string; name: string; signerOrder: number; }

const DocumentSigningDetailsDrawer: React.FC<Props> = ({ documentId, open, onClose, onRefresh }) => {
  const { documentSigningApiWrapper }: Api = useApi();
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();
  const navigate = useNavigate();

  const [doc, setDoc] = useState<any | null>(null);
  const [auditLog, setAuditLog] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  // signer edit state (only active when isDraft)
  const [editingSigners, setEditingSigners] = useState(false);
  const [signerRows, setSignerRows] = useState<SignerRow[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [savingSigners, setSavingSigners] = useState(false);

  const fetchDocument = async () => {
    setLoading(true);
    try {
      const [docRes, auditRes] = await Promise.all([
        documentSigningApiWrapper.getDocument('1.0', documentId),
        documentSigningApiWrapper.getAuditLog('1.0', documentId),
      ]);
      setDoc(docRes.data?.data);
      setAuditLog(auditRes.data?.data ?? []);
    } catch (err) {
      handleAPIError(err, toast, {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && documentId) {
      fetchDocument();
      setEditingSigners(false);
    }
  }, [open, documentId]);

  const startEditSigners = () => {
    const rows: SignerRow[] = (doc?.signers ?? []).map((s: any) => ({
      email: s.email,
      name: s.name ?? '',
      signerOrder: s.signerOrder ?? 0,
    }));
    setSignerRows(rows);
    setNewEmail('');
    setNewName('');
    setEditingSigners(true);
  };

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const addSignerRow = () => {
    const email = newEmail.trim();
    if (!email || !isValidEmail(email)) return;
    setSignerRows(prev => [...prev, { email, name: newName.trim(), signerOrder: prev.length }]);
    setNewEmail('');
    setNewName('');
  };

  const removeSignerRow = (idx: number) => {
    setSignerRows(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, signerOrder: i })));
  };

  const saveSigners = async () => {
    if (signerRows.length === 0) return;
    setSavingSigners(true);
    try {
      await documentSigningApiWrapper.addSigners('1.0', documentId, {
        signers: signerRows,
        signingOrder: doc?.signingOrder ?? false,
      });
      toast.success({ message: 'Signers saved.' });
      setEditingSigners(false);
      fetchDocument();
    } catch (err) {
      handleAPIError(err, toast, {});
    } finally {
      setSavingSigners(false);
    }
  };

  const handleSendInvitations = async () => {
    try {
      await documentSigningApiWrapper.sendInvitations('1.0', documentId);
      toast.success({ message: intl.get('documentSigning.invitationsSent') || 'Invitations sent.' });
      fetchDocument();
      onRefresh();
    } catch (err) {
      handleAPIError(err, toast, {});
    }
  };

  const handleResend = async (signerId: number) => {
    try {
      await documentSigningApiWrapper.resendInvitation('1.0', documentId, signerId);
      toast.success({ message: intl.get('documentSigning.invitationResent') || 'Invitation resent.' });
    } catch (err) {
      handleAPIError(err, toast, {});
    }
  };

  const handleCancel = () => {
    const dialogId = openDialog({
      type: 'confirm',
      title: intl.get('documentSigning.cancelTitle') || 'Cancel Document',
      message: intl.get('documentSigning.cancelConfirm') || 'Are you sure you want to cancel this document?',
      confirmButton: {
        confirmLabel: intl.get('common.confirm') || 'Confirm',
        handleConfirm: async () => {
          try {
            await documentSigningApiWrapper.cancelDocument('1.0', documentId);
            toast.success({ message: intl.get('documentSigning.cancelSuccess') || 'Document cancelled.' });
            closeDialog(dialogId);
            onClose();
            onRefresh();
          } catch (err) {
            handleAPIError(err, toast, {});
          }
        },
      },
    });
  };

  const stateName: string = doc?.workflowState?.name ?? '';
  const isDraft = stateName === 'Draft' || stateName === 'FieldsConfigured';
  const isPending = stateName === 'PendingSignatures' || stateName === 'SigningInProgress';
  const isCompleted = stateName === 'Completed';
  const isCancellable = ['Draft', 'FieldsConfigured', 'PendingSignatures', 'SigningInProgress'].includes(stateName);
  const hasSigners = (doc?.signers ?? []).length > 0;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 600, p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">{doc?.title ?? '...'}</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : doc ? (
          <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={intl.get(`documentSigning.workflowState.${stateName}`) || stateName}
                size="small"
                color={SIGNER_STATUS_COLOR[stateName.toLowerCase()] ?? 'default'}
              />
              {isCompleted && doc.signedS3Key && (
                <Button size="small" variant="outlined" href={`/api/v1.0/SigningDocument/${documentId}/signed`} target="_blank">
                  {intl.get('documentSigning.downloadSigned') || 'Download Signed PDF'}
                </Button>
              )}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <IconButton size="small" onClick={fetchDocument}><RefreshIcon /></IconButton>
                {isCancellable && (
                  <Button size="small" color="error" startIcon={<CancelIcon />} onClick={handleCancel}>
                    {intl.get('documentSigning.cancel') || 'Cancel'}
                  </Button>
                )}
                {isDraft && hasSigners && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => { onClose(); navigate(`/document-signing/${documentId}/fields`); }}
                  >
                    {intl.get('documentSigning.placeFields') || 'Place Fields'}
                  </Button>
                )}
                {isDraft && hasSigners && (
                  <Button size="small" variant="contained" startIcon={<SendIcon />} onClick={handleSendInvitations}>
                    {intl.get('documentSigning.sendInvitations') || 'Send Invitations'}
                  </Button>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 1 }} />

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label={intl.get('documentSigning.signers') || 'Signers'} />
              <Tab label={intl.get('documentSigning.auditLog') || 'Audit Log'} />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {isDraft && !editingSigners && (
                  <Button size="small" startIcon={<EditIcon />} onClick={startEditSigners} sx={{ mb: 1 }}>
                    {intl.get('documentSigning.editSigners') || 'Edit Signers'}
                  </Button>
                )}

                {isDraft && editingSigners ? (
                  <Stack spacing={1}>
                    {signerRows.map((s, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>{s.email}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{s.name}</Typography>
                        <IconButton size="small" onClick={() => removeSignerRow(idx)}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    ))}
                    <Divider />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small" label="Email *" value={newEmail}
                        onChange={e => setNewEmail(e.target.value)}
                        error={newEmail.trim().length > 0 && !isValidEmail(newEmail.trim())}
                        helperText={newEmail.trim().length > 0 && !isValidEmail(newEmail.trim()) ? 'Invalid email' : undefined}
                        sx={{ flex: 2 }}
                      />
                      <TextField size="small" label="Name" value={newName} onChange={e => setNewName(e.target.value)} sx={{ flex: 2 }} />
                      <Button size="small" startIcon={<AddIcon />} onClick={addSignerRow} disabled={!newEmail.trim() || !isValidEmail(newEmail.trim())}>
                        Add
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => setEditingSigners(false)}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={saveSigners} disabled={savingSigners || signerRows.length === 0}>
                        {savingSigners ? 'Saving...' : 'Save Signers'}
                      </Button>
                    </Box>
                  </Stack>
                ) : (!doc.signers || doc.signers.length === 0) ? (
                  <Alert severity="info">{intl.get('documentSigning.noSigners') || 'No signers added.'}</Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{intl.get('documentSigning.signer.name') || 'Name'}</TableCell>
                        <TableCell>{intl.get('documentSigning.signer.email') || 'Email'}</TableCell>
                        <TableCell>{intl.get('documentSigning.signer.status') || 'Status'}</TableCell>
                        <TableCell>{intl.get('documentSigning.signer.signedAt') || 'Signed At'}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {doc.signers.map((s: any) => (
                        <TableRow key={s.id}>
                          <TableCell>{s.name ?? '—'}</TableCell>
                          <TableCell>{s.email}</TableCell>
                          <TableCell>
                            <Chip
                              label={intl.get(`documentSigning.signer.status.${s.status}`) || s.status}
                              size="small"
                              color={SIGNER_STATUS_COLOR[s.status] ?? 'default'}
                            />
                          </TableCell>
                          <TableCell>{s.signedAt ? new Date(s.signedAt).toLocaleString() : '—'}</TableCell>
                          <TableCell>
                            {isPending && s.status === 'pending' && (
                              <Button size="small" onClick={() => handleResend(s.id)}>
                                {intl.get('documentSigning.resend') || 'Resend'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {auditLog.length === 0 ? (
                  <Alert severity="info">{intl.get('documentSigning.noAuditLog') || 'No audit events.'}</Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{intl.get('common.date') || 'Date'}</TableCell>
                        <TableCell>{intl.get('documentSigning.audit.action') || 'Action'}</TableCell>
                        <TableCell>{intl.get('documentSigning.audit.ip') || 'IP'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditLog.map((a: any) => (
                        <TableRow key={a.id}>
                          <TableCell>{new Date(a.createdDate).toLocaleString()}</TableCell>
                          <TableCell>{a.action}</TableCell>
                          <TableCell>{a.ipAddress ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}
          </>
        ) : null}
      </Box>
    </Drawer>
  );
};

export default DocumentSigningDetailsDrawer;
